/** SF-4845: the newest copies, with who started each, when, and how it ended. */
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Space, Table, Tag, Typography } from 'antd';
import { RUN_STATUS_LABELS, envCopyRunPath, envLabel } from '../constants';
import { envCopyErrorMessage, useEnvCopyRuns, type EnvCopyRun } from '../api/envCopyClient';
import { formatTime, starterName } from '../format';

export const RunStatusTag: React.FC<{ status: string }> = ({ status }) => {
  const s = RUN_STATUS_LABELS[status as keyof typeof RUN_STATUS_LABELS] ?? {
    label: status,
    color: 'default',
  };
  return <Tag color={s.color}>{s.label}</Tag>;
};

export const EnvCopyRunList: React.FC = () => {
  const query = useEnvCopyRuns(true);
  const loaded = query.data?.data?.runs;
  const runs = Array.isArray(loaded) ? loaded : undefined;

  return (
    <Card
      title="Recent copies"
      extra={
        <Button size="small" loading={query.isFetching} onClick={() => void query.refetch()}>
          Refresh
        </Button>
      }
    >
      {query.isError && !runs ? (
        <Space direction="vertical">
          <Alert
            type="error"
            showIcon
            message={envCopyErrorMessage(query.error, 'Could not load the recent copies.')}
          />
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Space>
      ) : (
        <>
          {query.isError && (
            <Typography.Text type="warning">
              Could not refresh. Showing the copies last loaded.
            </Typography.Text>
          )}
          <Table<EnvCopyRun>
            rowKey="run_id"
            size="small"
            loading={query.isLoading}
            dataSource={runs ?? []}
            pagination={false}
            locale={{ emptyText: query.isLoading ? ' ' : 'No copies yet.' }}
            columns={[
              {
                title: 'Copy',
                key: 'copy',
                render: (_, run) => (
                  <Link to={envCopyRunPath(run.run_id)}>
                    {envLabel(run.source)} to {envLabel(run.target)}
                  </Link>
                ),
              },
              {
                title: 'Started by',
                key: 'by',
                render: (_, run) => starterName(run.started_by),
              },
              {
                title: 'When',
                key: 'when',
                render: (_, run) => formatTime(run.created_at),
              },
              {
                title: 'Status',
                key: 'status',
                render: (_, run) => <RunStatusTag status={run.status} />,
              },
            ]}
          />
        </>
      )}
    </Card>
  );
};
